import { RangeSpan } from 'shared/excel/rangeSpan';
import moment from 'moment-timezone';

export const DATE_TIME_FORMAT_STRING = 'YYYY-MM-DD HH:mm:ss'; 
export const NEW_SHEET_MODE_DATE_TIME_FORMAT = 'D_MMM_HH_mm';

export function getCurrentDateTimeInExcelFormat(timezone) {
  return moment.tz(moment(), timezone).format(DATE_TIME_FORMAT_STRING)
}

export function parseCellAddress(cell) {
  const mc = cell.match(/^(\$)?([A-Za-z]+)?(\$)?(\d+)?$/);
  const error = new Error(`Invalid Cell Address '${cell}'`);
  if (!mc || !cell.length) { throw error; }

  const [, absoluteColumn, column, absoluteRow, row] = mc;
  if (!column && !row) { throw error; }
  const rowIndex = rowNumber2index(row);
  const columnIndex = columnLetter2index(column);
  // return { column: mc[1], row: +mc[2], columnIndex: letter2index(mc[1]), rowIndex: +mc[2] - 1 };
  return { rowIndex, columnIndex, row: +row, column, isRowIndexAbsolute: !!absoluteRow, isColumnIndexAbsolute: !!absoluteColumn };
}

export function getValidSingleCellAddress(address) {
  try {
    const { rowCount, columnCount, sheet, first } = rangeAddressToIndexes(address);
    if (1 === rowCount && 1 === columnCount) {
      return {
        sheet,
        first
      };
    }
    else {
      return null;
    }
  }
  catch (e) {
    return null;
  }
}


export function getCellAddress(colIndex, rowIndex) {
  return `${index2letter(colIndex)}${rowIndex + 1}`;
}

export function offsetCell(start, cols, rows) {
  const cell = parseCellAddress(start);
  return getCellAddress(cell.columnIndex + cols, cell.rowIndex + rows);
}

export function createRange(start, cols, rows) {
  if (cols < 0 || rows < 0) { throw new Error('Invalid range'); }
  return `${start}:${offsetCell(start, cols > 0 ? cols - 1 : cols, rows > 0 ? rows - 1 : rows)}`;
}

export function parseRangeAddress(fullAddress) {
  const { sheet, address } = parseFullRangeAddress(fullAddress);
  return { sheet, ...parsePartialRangeAddress(address) };
}

export function parseFullRangeAddress(fullAddress) {
  const mc = fullAddress.match(/^(?:([^!]+)!)?([^!]+)$/);
  if (!mc) { throw new Error(`Invalid full range address '${fullAddress}'`); }

  const [, sheet, address] = mc;

  return { sheet: unescapeWorksheetName(sheet ?? ''), address };
}

export function parsePartialRangeAddress(address) {
  const mc = address.match(/^([^:!]+)(?::([\$\w]+))?$/);
  if (!mc) { throw new Error(`Invalid partial range address '${address}'`); }

  let [, first, last] = mc;
  const isSingleCell = !last;
  last = last || first;

  return { first, last, isSingleCell };
}

export function normalizeWorksheetName(name = '') {
  const result = [];
  const name1 = name.toLowerCase();
  const name2 = name.toUpperCase();

  for (const i in name) {
    if (name1[i] !== name2[i] || name[i].match(/[0-9_ ]/)) {
      result.push(name[i]);
    }
  }
  return result.join('').replace(/^\s*[0-9]+/g, '').replace(/[^\S ]+/g, '_');
}

export function unescapeWorksheetName(worksheetName) {
  return worksheetName
    .replace(/^(')?(.*?)(')?$/, (match, q1, name, q2) => name)
    .replace(/('')/g, "'");
}

export function escapeWorksheetName(name) {
  const replaced = name.replace(/(')/g, "''");
  return replaced.includes(' ') ? `'${replaced}'` : replaced;
}

function index2letter(index) {
  let result = '';
  let colIndex = index + 1;
  while (colIndex > 0) {
    const modulo = (colIndex - 1) % 26;
    colIndex = (colIndex - modulo - 1) / 26;
    result = `${String.fromCharCode(modulo + 65)}${result}`;
  }
  return result;
}

function letter2index(letter) {
  const items = Array.from(letter).map(a => a.charCodeAt(0) - 64).reverse();

  return items.reduce((x, v, pow) => x + v * Math.pow(26, pow), 0) - 1;
}

function columnLetter2index(letter) {
  return letter ? letter2index(letter.toUpperCase()) : RangeSpan.Unbound.Index;
}

function columnIndex2letter(index) {
  return isUnboundIndex(index) ? '' : index2letter(index);
}

function rowNumber2index(numberString) {
  return numberString ? +numberString - 1 : RangeSpan.Unbound.Index;
}

function rowIndex2number(index) {
  return isUnboundIndex(index) ? '' : (index + 1).toString();
}

function getTargetCellIndex(index, count) {
  return isUnboundDim(index, count)
    ? RangeSpan.Unbound.Index
    : index + count - 1;
}

function getCellCount(startIndex, endIndex) {
  return isUnboundIndex(startIndex) || isUnboundIndex(endIndex)
    ? RangeSpan.Unbound.Count
    : endIndex - startIndex + 1;
}

export function rangeAddressToIndexes(address) {
  const { sheet, first, last, isSingleCell } = parseRangeAddress(address);
  const { rowIndex, columnIndex } = parseCellAddress(first);

  let columnCount = 1;
  let rowCount = 1;
  if (last) {
    const { rowIndex: r2, columnIndex: c2 } = parseCellAddress(last);
    columnCount = getCellCount(columnIndex, c2);
    rowCount = getCellCount(rowIndex, r2);
  }

  if (isSingleCell && isUnboundRange({ rowIndex, columnIndex, rowCount, columnCount })) {
    throw new Error(`Invalid unbound range address '${address}'`);
  }

  return { sheet, first, rowIndex, columnIndex, rowCount, columnCount };
}

export function isUnboundRange({ rowIndex, columnIndex, rowCount, columnCount }) {
  return [rowIndex, columnIndex, rowCount, columnCount].some(x => isUnboundIndex(x));
}

export function rangeIndexesToString({ rowIndex, columnIndex, rowCount = 1, columnCount = 1, sheet }) {
  const r1 = rowIndex2number(rowIndex);
  const c1 = columnIndex2letter(columnIndex);

  const sheetName = sheet ? `${escapeWorksheetName(sheet)}!` : '';
  const start = `${c1}${r1}`;
  let end = '';

  // You cannot use '1' for instance for range that contains the whole first row. You must use '1:1'
  if (rowCount > 1 || columnCount > 1 || isUnboundRange({ rowIndex, columnIndex, rowCount, columnCount })) {
    const r2 = rowIndex2number(getTargetCellIndex(rowIndex, rowCount));
    const c2 = columnIndex2letter(getTargetCellIndex(columnIndex, columnCount));

    end = r2.length || c2.length ? `:${c2}${r2}` : '';
  }

  return `${sheetName}${start}${end}`;
}

export function getNewSheetModeWorksheetName(flowstepName, dateTimeFormatted) {
  const maxLength = 31;
  const filler = '...';
  const name = normalizeWorksheetName(flowstepName);
  const capacity = maxLength - (dateTimeFormatted.length + 1); // 1 space
  const firstPart = capacity < name.length
    ? `${name.slice(0, capacity - filler.length)}${filler}`
    : name;
  return `${firstPart} ${dateTimeFormatted}`;
}


// TODO: REMOVE START

export function isUnboundIndex(index) {
  return index === RangeSpan.Unbound.Index;
}

export function isUnboundCount(count) {
  return count === RangeSpan.Unbound.Count;
}

export function isUnboundDim(index, count) {
  return isUnboundIndex(index) || isUnboundCount(count);
}

// TODO: REMOVE END


